import { ReactElement, useState } from 'react';
import Button from '../controls/Button';
import { initiatePaypalPayment, PaypalPaymentRequest } from '../services/dashboard-service';
import { useData } from '../context/data.context';

/**
 * Pricing plan page.
 * @returns - A page to display pricing plans.
 */
export default function Pricing(): ReactElement {
  const { activePaypalSubscription } = useData();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const planRequests: Record<string, PaypalPaymentRequest> = {
    Basic: {
      amount: 9.99,
      currency: 'USD',
      planName: 'Basic'
    },
    Advanced: {
      amount: 14.99,
      currency: 'USD',
      planName: 'Advanced'
    },
    Professional: {
      amount: 29.99,
      currency: 'USD',
      planName: 'Professional'
    }
  };

  /**
   * Handles plan checkout by initiating PayPal payment flow.
   * @param plan - Plan configuration to initiate checkout for.
   */
  const handlePlanCheckout = async (plan: PaypalPaymentRequest): Promise<void> => {
    setErrorMessage(null);
    setLoadingPlan(plan.planName);

    try {
      const approvalUrl = await initiatePaypalPayment(plan);
      window.location.assign(approvalUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to initiate PayPal payment', error);
      setErrorMessage('Unable to start PayPal checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const check = <div className="material-icons-outlined text-success-500">check_circle</div>;
  const dash = <span className="text-gray-600">-</span>;

  const currentPlanBadge = (
    <div className="inline-flex items-center justify-center gap-1.5 rounded border border-success-500 bg-success-500/15 px-[1.125rem] py-2.5 text-base font-semibold text-success-400 min-h-[2.75rem] min-w-[11.313rem] shadow-xs">
      <span className="material-icons-outlined text-base leading-none">check_circle</span>
      Current Plan
    </div>
  );

  const tdBase = 'font-normal text-gray-400 p-6 text-sm text-center';
  const tdLabel = 'font-medium text-gray-300 p-6 text-sm';

  return (
    <div className="my-6 mx-2 p-6 flex flex-1 flex-col bg-container-500 rounded">
      <div className="text-xl text-white font-semibold">We&apos;ve got plans just perfect for you!</div>
      <div className="text-gray-500 mt-1">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt{' '}
      </div>
      {errorMessage ? (
        <div
          role="alert"
          className="mt-4 rounded border border-error-500 bg-error-500/10 px-4 py-3 text-sm text-error-400"
        >
          {errorMessage}
        </div>
      ) : null}
      <table className="divide-y divide-gray-800 border border-gray-800 mt-8">
        <thead>
          <tr>
            <th className="text-xl text-gray-300 text-left px-6 font-semibold py-6 w-1/6">
              Pick a Plan
            </th>
            <th className="text-xl text-gray-300 text-left px-6 w-1/6">
              <div>Included</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div className="flex items-center">
                <div>Basic</div>
                <div className="ml-8 border border-primary-400 rounded-full py-1 px-3 text-sm text-primary-400">
                  Popular
                </div>
              </div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div>Advanced</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div>Professional</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div>Enterprise</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Pricing row */}
          <tr>
            <td> </td>
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">Free</div>
              <div className="text-gray-500 text-sm mt-1">Per month per device</div>
              <div className="mt-4">
                {!activePaypalSubscription ? currentPlanBadge : (
                  <Button size="lg" theme="secondary-color" name="Choose Plan" disabled />
                )}
              </div>
            </td>
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">$9.99</div>
              <div className="text-gray-500 text-sm mt-1">Per month per device</div>
              <div className="mt-4">
                {activePaypalSubscription?.planName === 'Basic' ? currentPlanBadge : (
                  <Button
                    size="lg"
                    theme="secondary-color"
                    name="Choose Plan"
                    onClick={() => handlePlanCheckout(planRequests.Basic)}
                    loading={loadingPlan === planRequests.Basic.planName}
                    disabled={loadingPlan !== null && loadingPlan !== planRequests.Basic.planName}
                  />
                )}
              </div>
            </td>
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">$14.99</div>
              <div className="text-gray-500 text-sm mt-1">Per month per device</div>
              <div className="mt-4">
                {activePaypalSubscription?.planName === 'Advanced' ? currentPlanBadge : (
                  <Button
                    size="lg"
                    theme="secondary-color"
                    name="Choose Plan"
                    onClick={() => handlePlanCheckout(planRequests.Advanced)}
                    loading={loadingPlan === planRequests.Advanced.planName}
                    disabled={loadingPlan !== null && loadingPlan !== planRequests.Advanced.planName}
                  />
                )}
              </div>
            </td>
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">$29.99</div>
              <div className="text-gray-500 text-sm mt-1">Per month per device</div>
              <div className="mt-4">
                {activePaypalSubscription?.planName === 'Professional' ? currentPlanBadge : (
                  <Button
                    size="lg"
                    theme="secondary-color"
                    name="Choose Plan"
                    onClick={() => handlePlanCheckout(planRequests.Professional)}
                    loading={loadingPlan === planRequests.Professional.planName}
                    disabled={loadingPlan !== null && loadingPlan !== planRequests.Professional.planName}
                  />
                )}
              </div>
            </td>
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">-</div>
              <div className="text-gray-500 text-sm mt-1">Email for quote</div>
              <div className="mt-4">
                <Button size="lg" theme="secondary-color" name="Choose Plan" />
              </div>
            </td>
          </tr>

          {/* Key Features header */}
          <tr className="border-b border-b-gray-800">
            <td className="px-6 text-sm text-primary-600 font-semibold py-4" colSpan={6}>
              Key Features
            </td>
          </tr>

          {/* Number of users */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>number of users</td>
            <td className={`${tdBase} bg-background-500`}>1</td>
            <td className={`${tdBase} bg-background-500`}>1</td>
            <td className={`${tdBase} bg-background-500`}>1</td>
            <td className={`${tdBase} bg-background-500`}>3</td>
            <td className={`${tdBase} bg-background-500`}>unlimited</td>
          </tr>

          {/* Data Rate */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>Data Rate</td>
            <td className={`${tdBase} bg-container-500`}>1 minute data</td>
            <td className={`${tdBase} bg-container-500`}>1 minute data</td>
            <td className={`${tdBase} bg-container-500`}>10 second data</td>
            <td className={`${tdBase} bg-container-500`}>Real-time data</td>
            <td className={`${tdBase} bg-container-500`}>Real-time data</td>
          </tr>

          {/* Data download */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Data download</td>
            <td className={`${tdBase} bg-background-500`}>No download</td>
            <td className={`${tdBase} bg-background-500`}>7 day of data storage and download</td>
            <td className={`${tdBase} bg-background-500`}>1 month of data storage and download</td>
            <td className={`${tdBase} bg-background-500`}>3 months of data storage and download</td>
            <td className={`${tdBase} bg-background-500`}>Unlimited data storage</td>
          </tr>

          {/* Analytics */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>Analytics</td>
            <td className={`${tdBase} bg-container-500`}>No analytics</td>
            <td className={`${tdBase} bg-container-500`}>Data -&gt; Raw Data, Devices</td>
            <td className={`${tdBase} bg-container-500`}>
              Data -&gt; Raw Data and Analytics (1 month max) and Devices page
            </td>
            <td className={`${tdBase} bg-container-500`}>
              Data -&gt; Raw Data and Analytics (3 months) and devices page
            </td>
            <td className={`${tdBase} bg-container-500`}>
              All pages under data; Analytics for entire duration of data
            </td>
          </tr>

          {/* Alerts */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Alerts</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>1 User</td>
            <td className={`${tdBase} bg-background-500`}>3 Users</td>
            <td className={`${tdBase} bg-background-500`}>Unlimited</td>
          </tr>

          {/* Report */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>Report</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>Basic reports</td>
            <td className={`${tdBase} bg-container-500`}>Customized reports</td>
          </tr>

          {/* BACnet Integration */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>BACnet Integration</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>BACnet-ready</td>
            <td className={`${tdBase} bg-background-500`}>BACnet-ready</td>
          </tr>

          {/* Insights: Ventilation analytics */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>
              Insights: Ventilation analytics (including ACH calculation, source location, etc)
            </td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>AI/ML learner model for ventilation control</td>
            <td className={`${tdBase} bg-container-500`}>AI/ML learner model for ventilation control</td>
          </tr>

          {/* Insights */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Insights</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>Basic insights</td>
            <td className={`${tdBase} bg-background-500`}>Advanced insights</td>
          </tr>

          {/* AI summary */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>AI summary</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>Front page</td>
            <td className={`${tdBase} bg-container-500`}>through out the dashboard</td>
            <td className={`${tdBase} bg-container-500`}>Custom AI queries and summaries</td>
          </tr>

          {/* Tailored dashboard */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Tailored dashboard</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{check}</td>
          </tr>

          {/* Sensor replacement and maintenance program */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>
              Sensor replacement and maintenance program
            </td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{check}</td>
          </tr>

          {/* Real-time fault detection diagnosis */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Real-time fault detection diagnosis</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{check}</td>
          </tr>

          {/* Data-driven guidance for ventilation system design */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>
              Data-driven guidance for ventilation system design
            </td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{check}</td>
          </tr>

          {/* Chat to sales */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500 py-10`}> </td>
            <td className={`${tdBase} bg-background-500 py-10`}> </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="mt-4 text-xs text-gray-500">
        All devices under 1 client must be in the same plan
      </div>
    </div>
  );
}
